import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ApproveApi } from 'apis/ApproveApis';
import { useTitle } from 'contexts/TitleProvider';
import { useUser } from 'contexts/UserProvider';
import useQueryParam from 'hooks/useQueryPrams';
import {
  IGetUserApprovalForPurchaseParams,
  IPurchaseApproval,
  IUserApproval,
  TGetUserApprovalDetail,
} from 'interfaces/Approve/Approve';
import path from 'routes/Path';
import { formatCurrency, toastError } from 'utils/constants/functions';
import ApprovalBoard, {
  IApprovalContentItem,
} from './Components/ApprovalBoard';
import ApproveDetailWrapper, {
  IUserApprovalInfo,
} from './Components/ApproveDetailWrapper';

const initData = {} as IUserApproval;
const initUserApproval = {} as IUserApprovalInfo;
const initPurchaseData = {} as IPurchaseApproval;
const initApprovalData = [] as IApprovalContentItem[];

const PricePurchaserApprove = () => {
  const navigate = useNavigate();
  const queryParam = useQueryParam();

  const { user } = useUser();
  const { setTitle, setBreadcrumb } = useTitle();

  const approvalId = queryParam.get('id');
  const [data, setData] = useState<IUserApproval>(initData);

  const [userApproval, setUserApproval] =
    useState<IUserApprovalInfo>(initUserApproval);
  const [purchaseData, setPurchaseData] =
    useState<IPurchaseApproval>(initPurchaseData);
  const [approvalData, setApprovalData] =
    useState<IApprovalContentItem[]>(initApprovalData);

  const formatCurrencyVND = useCallback((value: number) => {
    const formattedValue = formatCurrency({ isVND: true, value: value });

    return formattedValue !== null || formattedValue !== undefined
      ? `${formattedValue} VND`
      : '-';
  }, []);

  const { mutate: getUserApprovalById } = useMutation({
    mutationFn: (params: TGetUserApprovalDetail) =>
      ApproveApi.getUserApprovalById(params),
    onSuccess: (res) => {
      const item = res.data?.getUserApproval?.items?.[0];
      if (!Object.keys(item).length) {
        toastError('Không tìm thấy thông tin phê duyệt');
        return;
      }

      setData(item);
      getPurchaseApprovalMutate(item.idConnect);
      getUserApprovalForPurchaseMutate({
        approvalId: item.approvalId,
        idConnect: item.idConnect,
      });
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const {
    mutate: getUserApprovalForPurchaseMutate,
    isPending: isGetUserApprovalForPurchasePending,
  } = useMutation({
    mutationFn: (params: IGetUserApprovalForPurchaseParams) =>
      ApproveApi.getUserApprovalForPurchase(params),
    onSuccess: (res) => {
      if (res.data?.getUserApproval) {
        const item = (res.data?.getUserApproval?.items ?? [])[0];

        setUserApproval({
          plantId: item?.plantId,
          dateSent: item?.createDate,
          // status: item?.tstatusApproval?.name,
          status: item?.tuserApprovalDetail?.find(
            (item: any) => item?.email === user?.email,
          )?.approvalYn,
          fullName: item?.tcreateUser?.fullName,
          jobTitle: item?.tcreateUser?.jobTitle,
          note: '',
        });
      }
    },
    onError: (error) => {
      toastError(
        `Lỗi khi lấy thông tin người gửi phê duyệt. ${error?.message ?? error}`,
      );
    },
  });

  const {
    mutate: getPurchaseApprovalMutate,
    isPending: isGetPurchaseApprovalPending,
  } = useMutation({
    mutationFn: (purchaseId: string) =>
      ApproveApi.getPurchaseApproval(purchaseId),
    onSuccess: (res) => {
      if (res.data?.getPurchaseUserApproval)
        setPurchaseData(res.data?.getPurchaseUserApproval);
    },
    onError: (error) => {
      toastError(`Lỗi khi lấy thông tin thu mua. ${error?.message ?? error}`);
    },
  });

  // const { mutate: updateApprovalStatus } = useMutation({
  //   mutationFn: (params: IUpdateApprovalStatus) =>
  //     ApproveApi.updateApprovalStatus(params),
  //   onSuccess: () => {
  //     toastSuccess('Thành công');

  //     if (!state?.id) return;
  //     getUserApprovalById(state.id);
  //   },
  //   onError: (error) => {
  //     console.log(error);
  //     toastError(`Phê duyệt thất bại: ${error.message}`);
  //   },
  // });

  const refetch = useCallback(() => {
    if (!approvalId) return;

    const params: TGetUserApprovalDetail = {
      // id: state?.id,
      id: approvalId,
      ...(user?.isAdmin || user?.isHO
        ? {}
        : {
            tuserApprovalDetail: user?.email,
          }),
    };

    getUserApprovalById(params);
  }, [approvalId, user]);

  useEffect(() => {
    // if (!Object.keys(state).length) {
    //   navigate(path.APPROVE);
    //   return;
    // }

    setTitle('Thoả thuận giá bóng');
    setBreadcrumb([
      { title: 'Phê duyệt', href: path.APPROVE },
      { title: 'Thoả thuận giá bóng' },
    ]);

    // if (!state?.id) return;
    // getUserApprovalById(state.id);
  }, []);

  useEffect(() => {
    setApprovalData([
      {
        title: 'Người bán',
        children: [
          {
            content: [
              {
                title: 'Mã KH SAP',
                content: purchaseData?.tcustomer?.sapCustomerId,
              },
              {
                title: 'Tên KH',
                content: purchaseData?.tcustomer?.name,
              },
              {
                title: 'Bảng kê',
                content: (purchaseData?.tpurchaseOrderQ10 ?? [])
                  .map((item) => item.orderId)
                  .join('; '),
              },
            ],
          },
          {
            content: [
              {
                title: 'Mã KH DMS',
                content: purchaseData?.tcustomer?.id,
              },
              {
                title: 'Loại KH',
                content: purchaseData?.tcustomer?.trank?.name,
              },
            ],
          },
        ],
      },
      {
        title: 'Thông tin thu mua',
        children: [
          {
            content: [
              {
                title: 'SL Sản phẩm',
                content: purchaseData?.totalQuantity,
              },
              {
                title: 'Giá bóng hiện tại', //
                content: formatCurrencyVND(
                  purchaseData?.currentMartketGoldPrice,
                ),
              },
              {
                title: 'Giá bóng', //
                content: formatCurrencyVND(purchaseData?.marketGoldPrice),
              },
            ],
          },
          {
            content: [
              {
                title: 'Loại thu dẻ',
                content: purchaseData?.purchaseType,
              },
              {
                title: 'Chính sách',
                content: purchaseData?.policyType,
              },
              {
                title: 'Giá bóng TT', //
                content: formatCurrencyVND(purchaseData?.totalGoldPrice),
              },
            ],
          },
        ],
      },
      {
        title: '',
        children: [],
      },
      {
        title: '',
        children: [
          {
            content: [
              {
                title: 'TL Q10 mua',
                content: purchaseData?.totalQ10Buy,
              },
              {
                title: 'TL Q10 bán',
                content: purchaseData?.totalQ10Sale,
              },
            ],
          },
          {
            content: [
              {
                title: 'TL vôi',
                content: purchaseData?.q10LimeOut,
              },
              {
                title: 'Thành tiền ',
                content: formatCurrencyVND(purchaseData?.totalFinalPrice),
              },
            ],
          },
        ],
      },
      {
        title: 'Ghi chú',
        index: 3,
        children: [
          {
            content: [
              {
                title: '', //
                content: purchaseData?.note,
              },
            ],
          },
        ],
      },
      {
        index: 4,
        title: 'Chi tiết đơn',
        children: [
          {
            content: [
              {
                title: '',
                content: (
                  <p
                    className="text-[#0077CC] font-semibold no-underline text-sm cursor-pointer"
                    onClick={() =>
                      navigate(
                        `${path.ADD_NEW_PURCHASE_INVOICE}?id=${purchaseData.id}`,
                      )
                    }
                  >
                    Xem chi tiết
                  </p>
                ),
              },
            ],
          },
        ],
      },
    ]);
  }, [purchaseData]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  // const handleApprove = () => {
  //   if (!data?.tuserApprovalDetail?.length) {
  //     toastError('Không tim thấy thông tin phê duyệt!');
  //     return;
  //   }

  //   updateApprovalStatus({
  //     id: data?.tuserApprovalDetail?.[0]?.id,
  //     approvalYn: true,
  //   });
  // };

  // const handleCorrectionRequest = () => {
  //   if (!data?.tuserApprovalDetail?.length) {
  //     toastError('Không tim thấy thông tin phê duyệt!');
  //     return;
  //   }

  //   updateApprovalStatus({
  //     id: data?.tuserApprovalDetail?.[0]?.id,
  //     approvalYn: false,
  //   });
  // };

  return (
    <ApproveDetailWrapper
      data={data}
      userApproval={userApproval}
      title={`Bảng thu mua - ${data?.idConnect}`}
      loading={
        isGetPurchaseApprovalPending || isGetUserApprovalForPurchasePending
      }
      refetch={refetch}
      // approvalId={data?.id}
      // approvalStatus={data?.statusApproval}
      // refetch={() => getUserApprovalById(state.id)}
      // onApprove={handleApprove}
      // onCorrectionRequest={handleCorrectionRequest}
    >
      <ApprovalBoard data={approvalData} />
    </ApproveDetailWrapper>
  );
};

export default PricePurchaserApprove;
